/**
 * Sorting helpers for the dashboard `record_list` widget's object-template
 * attribute columns.
 *
 * Attribute columns are keyed `value.<attr>` (the order token the backend list
 * understands, e.g. `-value.priority`). Widget configs name them by the bare
 * attribute (`"priority"`), so column lookup accepts both forms.
 */

/** Find a column by its configured key, accepting `<attr>` for `value.<attr>`. */
export function findConfiguredColumn<T>(byKey: Map<string, T>, key: string): T | undefined {
    return byKey.get(key) ?? (key.startsWith("value.") ? undefined : byKey.get(`value.${key}`));
}

/** True when an order string (comma-separated, ±-prefixed) sorts by a `value.*` field. */
export function orderUsesValueField(order: string | null | undefined): boolean {
    if (!order) {
        return false;
    }
    return order
        .split(",")
        .map((part) => part.trim().replace(/^[+-]/, ""))
        .some((field) => field.startsWith("value."));
}

/**
 * The backend requires an `object_template_id eq <id>` condition whenever a
 * custom-object list is ordered by a `value.*` field. Add it to the widget's
 * filter (a JSON string) when the order needs it and the filter lacks it.
 */
export function withObjectTemplateFilter(
    filter: string | null,
    objectTemplateId: string | null,
    order: string | null | undefined,
): string | null {
    if (!objectTemplateId || !orderUsesValueField(order)) {
        return filter;
    }
    const otCondition = { field: "object_template_id", op: "eq", value: objectTemplateId };
    if (filter == null || filter === "") {
        return JSON.stringify(otCondition);
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(filter);
    } catch {
        return filter;
    }
    if (hasObjectTemplateCondition(parsed, objectTemplateId)) {
        return filter;
    }
    return JSON.stringify({ and: [otCondition, parsed] });
}

function hasObjectTemplateCondition(node: unknown, objectTemplateId: string): boolean {
    if (!node || typeof node !== "object") {
        return false;
    }
    const n = node as Record<string, unknown>;
    if (n.field === "object_template_id" && n.op === "eq" && n.value === objectTemplateId) {
        return true;
    }
    // Only a top-level AND guarantees the condition applies to every row.
    return Array.isArray(n.and) && n.and.some((child) => hasObjectTemplateCondition(child, objectTemplateId));
}
